<template>
  <div class="model-instances">
    <!-- 上段：模型 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>模型</span>
        <div class="table-header-right">
          <el-input
            v-model="keyword"
            class="keyword-input"
            size="small"
            placeholder="按名称 / ID 筛选"
            prefix-icon="el-icon-search"
            clearable
          />
          <span class="total-count">共 {{ filteredModels.length }} 个</span>
          <el-button
            type="danger"
            size="small"
            icon="el-icon-delete"
            :disabled="!selectedModels.length"
            :loading="deletingModels"
            @click="handleBatchDeleteModels"
          >{{ modelBatchLabel }}</el-button>
          <el-button type="primary" size="small" icon="el-icon-download" @click="importVisible = true">从 MinIO 导入</el-button>
          <el-button size="small" icon="el-icon-upload2" @click="uploadVisible = true">上传模型</el-button>
        </div>
      </div>

      <el-table
        ref="modelTable"
        v-loading="loadingModels"
        :data="filteredModels"
        :empty-text="modelEmptyText"
        row-key="id"
        highlight-current-row
        max-height="320"
        stripe
        border
        size="small"
        style="width:100%"
        @current-change="handleModelSelect"
        @row-click="handleModelRowClick"
        @selection-change="handleModelSelectionChange"
      >
        <el-table-column type="selection" width="46" />
        <el-table-column label="模型名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.name || row.file_path }}</template>
        </el-table-column>
        <el-table-column label="模型 ID" width="100">
          <template slot-scope="{ row }">
            <span class="mono">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="model_type" label="模型类型" width="100" align="center" />
        <el-table-column label="权重类型" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="weightTypeMeta(row.weight_type).tag" size="mini">{{ weightTypeMeta(row.weight_type).label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="来源" width="170">
          <template slot-scope="{ row }">
            <el-tag :type="sourceMeta(row.source).tag" size="mini">{{ sourceMeta(row.source).label }}</el-tag>
            <!-- 对象路径只对 MinIO 来源有意义 -->
            <div v-if="row.minio_object" class="cell-sub" :title="row.minio_object">{{ row.minio_object }}</div>
          </template>
        </el-table-column>
        <el-table-column label="版本" width="80" align="center">
          <template slot-scope="{ row }">{{ row.version || '-' }}</template>
        </el-table-column>
        <el-table-column label="阶段" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="stageMeta(row.stage).tag" size="mini">{{ stageMeta(row.stage).label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="引用" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.in_use" type="success" size="mini">实例在用</el-tag>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <!-- 模型有页面上传 / 从 MinIO 导入两条来源，「登记」是两个动作的共同说法 -->
        <el-table-column prop="uploaded_by" label="登记人" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.uploaded_by || '-' }}</template>
        </el-table-column>
        <el-table-column label="登记时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="handleUse(row)">新建实例</el-button>
            <el-button type="text" size="mini" @click="handleDetail(row)">详情</el-button>
            <el-button type="text" size="mini" class="danger-text" @click="handleDeleteModel(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 下段：实例，随上段选中的模型筛选 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <div class="table-header-left">
          <span>实例</span>
          <el-tag v-if="selectedModel" size="mini" closable @close="clearSelection">
            只看：{{ modelName(selectedModel.id) }}
          </el-tag>
        </div>
        <div class="table-header-right">
          <span class="total-count">共 {{ visibleInstances.length }} 个</span>
          <el-button
            type="danger"
            size="small"
            icon="el-icon-delete"
            :disabled="!selectedInstances.length"
            :loading="deletingInstances"
            @click="handleBatchDeleteInstances"
          >{{ instanceBatchLabel }}</el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新建实例</el-button>
        </div>
      </div>

      <el-table
        v-loading="loadingInstances"
        :data="visibleInstances"
        :empty-text="instanceEmptyText"
        stripe
        border
        size="small"
        style="width:100%"
        @selection-change="handleInstanceSelectionChange"
      >
        <el-table-column type="selection" width="46" :selectable="canDeleteInstance" />
        <el-table-column prop="name" label="实例名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="模型" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ modelName(row.model_id) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="stateMeta(row.state).tag" size="mini">{{ stateMeta(row.state).label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="GPU" width="80" align="center">
          <template slot-scope="{ row }">{{ gpuLabel(row.gpu_ids) }}</template>
        </el-table-column>
        <el-table-column label="最近推理" width="100" align="right">
          <template slot-scope="{ row }">{{ formatMs(row.last_inference_ms) }}</template>
        </el-table-column>
        <el-table-column prop="created_by" label="创建人" width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.created_by || '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.state === 'ready'"
              type="text"
              size="mini"
              @click="handleToggleInstance(row, false)"
            >停止</el-button>
            <el-button
              v-else
              type="text"
              size="mini"
              :disabled="row.state === 'loading'"
              @click="handleToggleInstance(row, true)"
            >启动</el-button>
            <el-button type="text" size="mini" :disabled="isLocked(row)" @click="openEdit(row)">编辑</el-button>
            <el-button
              type="text"
              size="mini"
              class="danger-text"
              :disabled="isLocked(row)"
              @click="handleDeleteInstance(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <instance-form-dialog
      :visible.sync="dialogVisible"
      :models="models"
      :gpus="gpus"
      :editing="editingInstance"
      :preset-model-id="presetModelId"
      @saved="handleInstanceSaved"
    />
    <model-upload-dialog :visible.sync="uploadVisible" @uploaded="loadModels" />
    <model-import-dialog :visible.sync="importVisible" @imported="loadModels" />
    <model-detail-drawer :visible.sync="detailVisible" :model="detailModel" @updated="loadModels" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import InstanceFormDialog from '../../components/InstanceFormDialog.vue';
import ModelUploadDialog from '../../components/ModelUploadDialog.vue';
import ModelImportDialog from '../../components/ModelImportDialog.vue';
import ModelDetailDrawer from '../../components/ModelDetailDrawer.vue';
import {
  fetchInferenceStatus,
  fetchModels,
  deleteModel,
  deleteInstance,
  startInstance,
  stopInstance,
} from '../../api/blissrose/inference';
import { INSTANCE_STATES, WEIGHT_TYPES } from '../../utils/labels';

const POLL_INTERVAL_MS = 10000;

const STAGE_META = {
  staging: { label: '待上线', tag: 'info' },
  production: { label: '生产', tag: 'success' },
  archived: { label: '已归档', tag: 'warning' },
};

const SOURCE_META = {
  upload: { label: '上传', tag: 'info' },
  minio: { label: 'MinIO', tag: 'success' },
  deploy: { label: '部署', tag: 'warning' },
};

/** 模型实例：上段模型、下段实例的主从页；点中模型把下段筛成它的实例，模型名由本地补上 */
export default {
  name: 'ModelInstances',

  components: { InstanceFormDialog, ModelUploadDialog, ModelImportDialog, ModelDetailDrawer },

  data() {
    return {
      loadingModels: false,
      loadingInstances: false,
      models: [],
      keyword: '',
      selectedModelId: '',
      instances: [],
      defaultInstanceId: '',
      gpus: [],
      dialogVisible: false,
      editingInstance: null,
      presetModelId: '',
      uploadVisible: false,
      importVisible: false,
      detailVisible: false,
      detailModel: null,
      // 两张表各自的批量删除勾选与进行态
      selectedModels: [],
      selectedInstances: [],
      deletingModels: false,
      deletingInstances: false,
      pollTimer: null,
    };
  },

  computed: {
    modelBatchLabel() {
      return this.selectedModels.length ? `删除选中（${this.selectedModels.length}）` : '删除选中';
    },

    instanceBatchLabel() {
      return this.selectedInstances.length ? `删除选中（${this.selectedInstances.length}）` : '删除选中';
    },

    /** 关键字在本地过滤，不跑后端 */
    filteredModels() {
      const kw = this.keyword.trim().toLowerCase();
      if (!kw) return this.models;
      return this.models.filter(m => {
        const fields = [m.id, m.name, m.file_path, m.model_type];
        return fields.filter(Boolean).some(v => String(v).toLowerCase().includes(kw));
      });
    },

    /** 上段选中的模型；被删掉时返回 null，界面显示「全部实例」 */
    selectedModel() {
      if (!this.selectedModelId) return null;
      return this.models.find(m => m.id === this.selectedModelId) || null;
    },

    /** 下段展示的实例；内置实例（default_instance_id）不列 */
    visibleInstances() {
      const list = this.instances.filter(item => item.id !== this.defaultInstanceId);
      if (!this.selectedModel) return list;
      return list.filter(item => item.model_id === this.selectedModel.id);
    },

    modelEmptyText() {
      return this.models.length ? '没有匹配的模型' : '还没有模型';
    },

    instanceEmptyText() {
      return '暂无数据';
    },
  },

  async mounted() {
    await this.loadAll();
    this.applyQueryModel();
    // 轮询反映启停的后台完成结果；有勾选时停一轮，避免换数据打断勾选
    this.pollTimer = setInterval(() => {
      if (!this.loadingInstances && !this.selectedInstances.length) this.loadInstances(true);
    }, POLL_INTERVAL_MS);
  },

  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },

  methods: {
    async loadAll() {
      await Promise.all([this.loadInstances(), this.loadModels()]);
    },

    /** 拉取实例列表（含运行时状态）与 GPU 信息；silent 为定时轮询，不显示加载态 */
    async loadInstances(silent = false) {
      if (!silent) this.loadingInstances = true;
      try {
        const { instances, defaultInstanceId, gpus } = (await fetchInferenceStatus()).data;
        this.instances = instances || [];
        this.defaultInstanceId = defaultInstanceId || '';
        this.gpus = gpus || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        if (!silent) this.loadingInstances = false;
      }
    },

    async loadModels() {
      this.loadingModels = true;
      try {
        const { items } = (await fetchModels()).data;
        this.models = items || [];
        // 抽屉开着时把引用换到刚拉到的这条
        if (this.detailModel) {
          this.detailModel = this.models.find(m => m.id === this.detailModel.id) || this.detailModel;
        }
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.loadingModels = false;
      }
    },

    // ====== 展示辅助 ======

    stateMeta(state) {
      return INSTANCE_STATES[state] || INSTANCE_STATES.stopped;
    },

    stageMeta(stage) {
      return STAGE_META[stage] || STAGE_META.staging;
    },

    /** 未知来源按原值显示 */
    sourceMeta(source) {
      return SOURCE_META[source] || { label: source || '-', tag: 'info' };
    },

    /** 未知性质按原值显示 */
    weightTypeMeta(weightType) {
      return WEIGHT_TYPES[weightType] || { label: weightType || '-', tag: 'info' };
    },

    /** 已进显存或正在进（ready/loading）时配置不可改；异常态不锁 */
    isLocked(row) {
      return row.state === 'ready' || row.state === 'loading';
    },

    modelName(modelId) {
      const hit = this.models.find(m => m.id === modelId);
      if (hit) return hit.name || hit.file_path;
      return modelId || '-';
    },

    gpuLabel(gpuIds) {
      return gpuIds && gpuIds.length ? gpuIds.join(', ') : 'CPU';
    },

    formatMs(value) {
      return typeof value === 'number' ? `${value.toFixed(0)} ms` : '-';
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },

    // ====== 主从联动 ======

    /** 选中模型：表格高亮与下段筛选 id 一起更新 */
    selectModel(row) {
      this.selectedModelId = row.id;
      this.$refs.modelTable.setCurrentRow(row);
    },

    handleModelSelect(row) {
      this.selectedModelId = row ? row.id : '';
    },

    /** 再点已选中行＝取消筛选；放过行内按钮与勾选框的冒泡点击 */
    handleModelRowClick(row, column, event) {
      if (row.id !== this.selectedModelId) return;
      if (event.target.closest('.el-button') || event.target.closest('.el-checkbox')) return;
      this.$refs.modelTable.setCurrentRow(null);
    },

    clearSelection() {
      this.$refs.modelTable.setCurrentRow(null);
      this.selectedModelId = '';
    },

    /** 用该模型建实例：选中并预置进新建表单 */
    handleUse(row) {
      this.selectModel(row);
      this.openCreate();
    },

    /** 只把整条记录递给详情抽屉（抽屉自己拉清单） */
    handleDetail(row) {
      this.detailModel = row;
      this.detailVisible = true;
    },

    async handleDeleteModel(row) {
      try {
        await this.$confirm(`确定删除模型「${row.name || row.file_path}」吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
      } catch (err) {
        return;
      }
      try {
        // 被推理实例引用的模型删不掉，原因由后端返回
        await deleteModel(row.id);
        this.$message.success('模型已删除');
        await this.loadModels();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    handleModelSelectionChange(rows) {
      this.selectedModels = rows || [];
    },

    /** 批量删除模型：逐个调接口，失败的点名带出 */
    async handleBatchDeleteModels() {
      const rows = [...this.selectedModels];
      if (!rows.length) return;
      try {
        await this.$confirm(
          `将删除选中的 ${rows.length} 个模型：模型记录与磁盘上的权重文件一并删掉，`
            + '不可恢复。确定删除？',
          '删除模型',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' },
        );
      } catch (err) {
        return;
      }
      this.deletingModels = true;
      const failed = [];
      for (const row of rows) {
        try {
          await deleteModel(row.id);
        } catch (err) {
          failed.push(row.name || row.file_path || row.id);
        }
      }
      this.deletingModels = false;
      const done = rows.length - failed.length;
      if (failed.length) {
        this.$message.warning(`已删除 ${done} 个；${failed.length} 个没删掉：${failed.join('、')}`);
      } else {
        this.$message.success(`已删除 ${done} 个模型`);
      }
      await this.loadModels();
    },

    // ====== 实例增删改启停 ======

    openCreate() {
      this.editingInstance = null;
      this.presetModelId = this.selectedModelId || '';
      this.dialogVisible = true;
    },

    openEdit(row) {
      this.editingInstance = row;
      this.presetModelId = '';
      this.dialogVisible = true;
    },

    /** 实例增删改影响模型「引用」标记，两段一起重载 */
    async handleInstanceSaved() {
      await Promise.all([this.loadInstances(), this.loadModels()]);
    },

    async handleDeleteInstance(row) {
      try {
        await this.$confirm(`确定删除实例「${row.name}」吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
      } catch (err) {
        return;
      }
      try {
        await deleteInstance(row.id);
        this.$message.success('实例已删除');
        await this.handleInstanceSaved();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    handleInstanceSelectionChange(rows) {
      this.selectedInstances = rows || [];
    },

    /** 跑着的不给勾，判据同行内删除按钮 */
    canDeleteInstance(row) {
      return !this.isLocked(row);
    },

    /** 批量删除实例：逐个调接口，失败的点名带出 */
    async handleBatchDeleteInstances() {
      const rows = [...this.selectedInstances];
      if (!rows.length) return;
      try {
        await this.$confirm(
          `将删除选中的 ${rows.length} 个实例，不可恢复。确定删除？`,
          '删除实例',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' },
        );
      } catch (err) {
        return;
      }
      this.deletingInstances = true;
      const failed = [];
      for (const row of rows) {
        try {
          await deleteInstance(row.id);
        } catch (err) {
          failed.push(row.name || row.id);
        }
      }
      this.deletingInstances = false;
      const done = rows.length - failed.length;
      if (failed.length) {
        this.$message.warning(`已删除 ${done} 个；${failed.length} 个没删掉：${failed.join('、')}`);
      } else {
        this.$message.success(`已删除 ${done} 个实例`);
      }
      await this.handleInstanceSaved();
    },

    /** 启动＝权重读进显存预热，停止＝释放显存；加载耗时由轮询反映 */
    async handleToggleInstance(row, isStart) {
      try {
        await (isStart ? startInstance(row.id) : stopInstance(row.id));
        this.$message.success(isStart ? '已下发启动指令' : '已下发停止指令');
        await this.loadInstances();
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    /** 旧链 /blissrose/inference?model_id=X 转发到本页：选中该模型并打开新建对话框；读完即清 query */
    applyQueryModel() {
      const modelId = this.$route.query.model_id;
      if (!modelId) return;
      this.$router.replace({ path: this.$route.path, query: {} });
      const hit = this.models.find(m => m.id === modelId);
      if (!hit) return;
      this.selectModel(hit);
      this.openCreate();
    },
  },
};
</script>

<style scoped>
.table-card {
  margin-bottom: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.keyword-input {
  width: 220px;
}

.total-count {
  color: #909399;
  font-size: 13px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #606266;
}

.cell-sub {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted {
  color: #c0c4cc;
}

.danger-text {
  color: #f56c6c;
}
</style>
